import { Box } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  DataGrid,
  getDefaultGridFilterModel,
  gridPaginationModelSelector,
  gridPaginationRowCountSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid';
import getSortParametersFactory from 'components/@home/@documents/getSortParametersFactory';
import GridToolbarSearch from 'components/common/GridToolbarSearch';
import Loading from 'components/common/Loading';
import Pagination from 'components/controls/Pagination';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import merge from 'lodash/merge';
import { array, bool, func, number, object, oneOfType, string } from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import toQueryString from 'utils/toQueryString';
import { shouldIgnoreStaleUrlSync } from './urlSearchSync';

const LoadingOverlay = () => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        backgroundColor: alpha(theme.palette.background.paper, 0.8),
        zIndex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Loading size={30} />
    </Box>
  );
};

const CustomFooter = ({ totalRowCount: propTotalRowCount = undefined }) => {
  const apiRef = useGridApiContext();
  const { page, pageSize } = useGridSelector(apiRef, gridPaginationModelSelector);
  const paginationRowCount = useGridSelector(apiRef, gridPaginationRowCountSelector);

  const totalRowCount = propTotalRowCount !== undefined ? propTotalRowCount : paginationRowCount;

  const handlePageChange = ({ selected }) => {
    apiRef.current.setPaginationModel({ page: selected, pageSize });
  };

  return (
    <Box sx={{ pt: 2, pb: 0.5 }}>
      <Pagination
        total={totalRowCount}
        page={page}
        perPage={pageSize}
        onChange={handlePageChange}
      />
    </Box>
  );
};

CustomFooter.propTypes = {
  totalRowCount: number,
};

export const parseURLQuery = (location, defaultSortModel) => {
  const { p, sf, sd, t, s, f } = Object.fromEntries(new URLSearchParams(location.search)) || {};
  const page = +p || 0;
  const field = sf || defaultSortModel?.[0].field;
  const sort = sd || defaultSortModel?.[0].sort;
  const filterModel = getDefaultGridFilterModel();
  if (f) {
    try {
      const parsedFilterModel = JSON.parse(f);
      if (Array.isArray(parsedFilterModel)) {
        filterModel.items = parsedFilterModel;
      }
    } catch (e) {
      console.error(e);
    }
  }
  return {
    sortModel: [{ field, sort }],
    page,
    ...(t ? { type: t } : {}),
    filterModel: {
      ...filterModel,
      quickFilterValues: s ? s.split(',') : filterModel?.quickFilterValues || [],
    },
  };
};

const OmmnioDataGrid = ({
  slots = {},
  slotProps = {},
  sx = {},
  onPageSizeChange = null,
  onPageChange = null,
  onSortModelChange = null,
  onFilterModelChange = null,
  initialState = {},
  updateHistory = false,
  defaultSortModel = [{ field: 'name', sort: 'asc' }],
  page: pageProp = 0,
  pageSize: pageSizeProp = -1,
  resetPaginationKey = undefined,
  rows = [],
  disableEvenOdd = false,
  extraUrlParams = {},
  rowCount = undefined,
  loading = false,
  ...props
}) => {
  const { paginationMode = 'client' } = props;
  const isServerPagination = paginationMode === 'server';
  const location = useLocation();
  const getSortParameters = getSortParametersFactory(defaultSortModel);
  const [page, setPage] = useState(pageProp);
  const [pageSize, setPageSize] = useState(pageSizeProp);
  const pageSizeLockedRef = useRef(pageSizeProp > 1);
  const [autoPageSizeEnabled, setAutoPageSizeEnabled] = useState(pageSizeProp <= 1);

  useEffect(() => {
    setPage(pageProp);
  }, [pageProp]);

  useEffect(() => {
    if (pageSizeProp > 0) {
      setPageSize(pageSizeProp);
      if (pageSizeProp > 1) {
        pageSizeLockedRef.current = true;
        setAutoPageSizeEnabled(false);
      }
    }
  }, [pageSizeProp]);

  useEffect(() => {
    if (resetPaginationKey !== undefined) {
      setPage(0);
      if (typeof onPageChange === 'function') {
        onPageChange(0);
      }
    }
  }, [resetPaginationKey, onPageChange]);
  const [sortModel, setSortModel] = useState(defaultSortModel);
  const { filterModel: defaultGridFilterModel } = getDefaultGridFilterModel();
  const [filterModel, setFilterModel] = useState(defaultGridFilterModel);
  const navigate = useNavigate();
  const locationRef = useRef(location);
  locationRef.current = location;
  // Search string passed to the latest navigate(), and the history entry it
  // started from. Router 7 flushes that update in a transition.
  const pendingSearchRef = useRef(null);
  const pendingLocationKeyRef = useRef(null);

  const navigateToSearch = (search, replace) => {
    const nextSearch = search || '';
    pendingSearchRef.current = nextSearch;
    pendingLocationKeyRef.current = locationRef.current.key;
    navigate(
      { search: nextSearch, state: locationRef.current.state },
      replace ? { replace: true } : undefined,
    );
  };

  useEffect(() => {
    if (!updateHistory) return undefined;
    const timeoutId = setTimeout(() => {
      const currentLocation = locationRef.current;
      if (
        shouldIgnoreStaleUrlSync({
          locationSearch: currentLocation.search,
          pendingSearch: pendingSearchRef.current,
          locationKey: currentLocation.key,
          pendingLocationKey: pendingLocationKeyRef.current,
        })
      ) {
        return;
      }
      pendingSearchRef.current = null;
      pendingLocationKeyRef.current = null;
      const query = parseURLQuery(currentLocation, defaultSortModel);
      const {
        page: parsedPage = 0,
        sortModel: parsedSortModel,
        filterModel: parsedFilterModel,
      } = query;
      if (page !== parsedPage) {
        setPage(parsedPage);
        if (typeof onPageChange === 'function') {
          onPageChange(parsedPage);
        }
      }
      if (!isEqual(sortModel, parsedSortModel)) {
        setSortModel(parsedSortModel);
        if (typeof onSortModelChange === 'function') {
          onSortModelChange(parsedSortModel);
        }
      }
      if (!isEqual(filterModel, parsedFilterModel)) {
        setFilterModel(parsedFilterModel);
        if (typeof onFilterModelChange === 'function') {
          onFilterModelChange(parsedFilterModel);
        }
      }
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [
    defaultGridFilterModel,
    defaultSortModel,
    filterModel,
    location,
    onFilterModelChange,
    onPageChange,
    onSortModelChange,
    page,
    sortModel,
    updateHistory,
  ]);

  const handlePaginationModelChange = ({ page: newPage, pageSize: newPageSize }) => {
    if (newPageSize > 1 && !pageSizeLockedRef.current) {
      pageSizeLockedRef.current = true;
      setAutoPageSizeEnabled(false);
      if (typeof onPageSizeChange === 'function') {
        onPageSizeChange(newPageSize);
      }
      setPageSize(newPageSize);
    }
    if (typeof onPageChange === 'function') {
      onPageChange(newPage);
    }
    if (updateHistory && newPage !== page) {
      navigateToSearch(
        toQueryString({
          ...(newPage === 0 ? {} : { p: newPage }),
          ...getSortParameters(sortModel),
          ...(isEmpty(filterModel?.quickFilterValues) ? {} : { s: filterModel.quickFilterValues }),
          ...(filterModel?.items?.length
            ? {
                f: JSON.stringify(
                  filterModel.items.map(({ field, operator, value }) => ({
                    field,
                    operator,
                    value,
                  })),
                ),
              }
            : {}),
          ...extraUrlParams,
        }),
      );
    } else {
      setPage(newPage);
    }
  };

  const handleSortModelChange = (newSortModel, ...rest) => {
    if (typeof onSortModelChange === 'function') {
      onSortModelChange(newSortModel, ...rest);
    }
    if (updateHistory && !isEqual(newSortModel, sortModel)) {
      navigateToSearch(
        toQueryString({
          ...getSortParameters(newSortModel),
          ...(isEmpty(filterModel?.quickFilterValues) ? {} : { s: filterModel.quickFilterValues }),
          ...(filterModel?.items?.length
            ? {
                f: JSON.stringify(
                  filterModel.items.map(({ field, operator, value }) => ({
                    field,
                    operator,
                    value,
                  })),
                ),
              }
            : {}),
          ...extraUrlParams,
        }),
        true,
      );
    } else {
      setSortModel(newSortModel);
    }
  };

  const handleFilterModelChange = (newFilterModel, ...rest) => {
    if (typeof onFilterModelChange === 'function') {
      onFilterModelChange(newFilterModel, ...rest);
    }
    if (updateHistory && !isEqual(newFilterModel, filterModel)) {
      navigateToSearch(
        toQueryString({
          ...getSortParameters(sortModel),
          ...(isEmpty(newFilterModel?.quickFilterValues)
            ? {}
            : { s: newFilterModel.quickFilterValues }),
          ...(newFilterModel?.items?.length
            ? {
                f: JSON.stringify(
                  newFilterModel.items.map(({ field, operator, value }) => ({
                    field,
                    operator,
                    value,
                  })),
                ),
              }
            : {}),
          ...extraUrlParams,
        }),
        true,
      );
    }
    setFilterModel(newFilterModel);
  };
  return (
    <DataGrid
      sx={[
        sx,
        disableEvenOdd
          ? {
              '& .MuiDataGrid-main': {
                '& .MuiDataGrid-row': {
                  '&:nth-of-type(odd), &:hover, &.Mui-selected': {
                    backgroundColor: 'transparent',
                  },
                },
              },
            }
          : {},
      ]}
      initialState={merge({ sorting: { sortModel: defaultSortModel } }, initialState)}
      sortingOrder={['asc', 'desc']}
      autoPageSize={autoPageSizeEnabled}
      disableColumnMenu
      showToolbar
      slots={{
        footer: CustomFooter,
        toolbar: GridToolbarSearch,
        loadingOverlay: LoadingOverlay,
        ...slots,
      }}
      slotProps={{
        footer: {
          // Server-side grids need an explicit total; client-side grids use row count from `rows`.
          ...(isServerPagination && typeof rowCount === 'number'
            ? { totalRowCount: rowCount }
            : {}),
        },
        ...slotProps,
      }}
      {...props}
      {...(isServerPagination && typeof rowCount === 'number' ? { rowCount } : {})}
      loading={loading}
      rows={rows}
      paginationModel={{ page, pageSize }}
      onPaginationModelChange={handlePaginationModelChange}
      sortModel={sortModel}
      filterModel={filterModel}
      onFilterModelChange={handleFilterModelChange}
      onSortModelChange={handleSortModelChange}
    />
  );
};

OmmnioDataGrid.propTypes = {
  sx: object,
  slots: object,
  slotProps: object,
  rowCount: number,
  rows: array,
  onPageSizeChange: func,
  onPageChange: func,
  onSortModelChange: func,
  onFilterModelChange: func,
  initialState: object,
  updateHistory: bool,
  defaultSortModel: array,
  page: number,
  pageSize: number,
  resetPaginationKey: oneOfType([string, number]),
  disableEvenOdd: bool,
  loading: bool,
  extraUrlParams: object,
};
export default OmmnioDataGrid;
